import React, { Suspense, useContext, useState } from "react";
import FullLoader from "components/loader/full-loader";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDeleteData, useFetchData } from "hooks/useApiHooks";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { AppContext } from "contexts/AppContext";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import CreatePage from "./components/create-page";
import { useDeleteHandler } from "hooks/useDeleteHandler";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import AssignRole from "./components/assign-role";
import AssignUser from "./components/sync-user";
import { UserProvider } from "contexts/v1/masterData/UserContext";

const SearchInput = ({ onSearch, defaultValue }) => {
  const [search, setSearch] = useState(defaultValue || "");

  return (
    <div className="d-flex gap-2">
      <div className="input-icon flex-fill">
        <input
          type="text"
          className="form-control"
          placeholder="Search role..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSearch(e.target.value);
          }}
        />
        <span className="input-icon-addon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="icon icon-tabler icon-tabler-search"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <circle cx="10" cy="10" r="7" />
            <line x1="21" y1="21" x2="15" y2="15" />
          </svg>
        </span>
      </div>
      <button type="button" className="btn btn-outline-primary" onClick={() => onSearch(search)}>
        Cari
      </button>
    </div>
  );
};

const getUserInitials = (name = "") => {
  const nameParts = name.trim().split(" ").filter(Boolean);
  if (nameParts.length > 1) return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
  return (nameParts[0] || "U").slice(0, 2).toUpperCase();
};

const RoleUsersPreview = ({ users = [], totalCount = 0 }) => {
  const visibleUsers = users.slice(0, 5);
  const safeTotal = totalCount || users.length;
  const hiddenCount = Math.max(0, safeTotal - visibleUsers.length);

  if (!safeTotal) {
    return <span className="text-muted small">Belum ada user</span>;
  }

  return (
    <div className="role-users-preview" style={{ minWidth: "150px", maxWidth: "220px" }}>
      <div className="d-flex align-items-center flex-nowrap" style={{ overflow: "hidden" }}>
        {visibleUsers.map((user, index) => (
          <OverlayTrigger
            key={user.id || index}
            placement="top"
            overlay={
              <Tooltip id={`role-user-${user.id || index}`}>
                {user.name}
              </Tooltip>
            }
          >
            <span
              className="rounded-circle bg-primary text-white d-flex justify-content-center align-items-center border border-white flex-shrink-0"
              style={{
                width: "26px",
                height: "26px",
                fontSize: "10px",
                fontWeight: "bold",
                marginLeft: index === 0 ? 0 : "-6px",
                zIndex: visibleUsers.length - index,
              }}
            >
              {getUserInitials(user.name)}
            </span>
          </OverlayTrigger>
        ))}
        {hiddenCount > 0 && (
          <span
            className="badge bg-secondary-lt text-secondary ms-1 flex-shrink-0"
            style={{ fontSize: "10px" }}
          >
            +{hiddenCount}
          </span>
        )}
      </div>
      <div className="text-muted small mt-1 text-nowrap">{safeTotal} user</div>
    </div>
  );
};

const RolesPage = () => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const queryString = buildQueryString({
    search: search || "",
    page: currentPage,
    limit: LIMIT_DATA_PER_PAGE,
    include: "usersCount,permissionsCount,users,permissions",
  });

  useFetchData(`/role${queryString}`, "roles");
  const { deleteData } = useDeleteData("/role", "roles", queryString);

  const { state } = useContext(AppContext);
  const { roles } = state;

  const handleDelete = useDeleteHandler(
    deleteData,
    `Role has been deleted`,
    `Failed to delete Role`
  );
  const { handleSuccess, handleError, showCanvasHandler } = useCanvasHandler();

  const handleCreate = (e) => {
    e.preventDefault();
    showCanvasHandler(
      "Create Role",
      <CreatePage onSuccess={handleSuccess} onError={handleError} />
    );
  };

  const handleAssignPermission = (e, data) => {
    e.preventDefault();
    showCanvasHandler(
      "Assign Permission",
      <AssignRole
        initialData={data}
        onSuccess={handleSuccess}
        onError={handleError}
      />,
      "large-canvas"
    );
  };

  // Refetch roles data
  const { refetch: refetchRoles } = useFetchData(
    `/role${queryString}`,
    "roles"
  );

  const handleAssignUser = (e, data) => {
    e.preventDefault();
    showCanvasHandler(
      "Sync User",
      <UserProvider>
        <AssignUser
          initialData={data}
          onSuccess={() => {
            handleSuccess();
            // Refetch roles after assigning user
            refetchRoles();
          }}
          onError={handleError}
        />
      </UserProvider>,
      "large-canvas"
    );
  };

  // const handleUpdate = (e, data) => {
  //     e.preventDefault();
  //     showCanvasHandler('Update Label',
  //         <CreatePage
  //             initialData={data}
  //             onSuccess={handleSuccess}
  //             onError={handleError}
  //         />
  //     );
  // };

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const totalPages = roles.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages,
    currentPage,
    onPageChange: setCurrentPage,
    visiblePages: 5, // Optional: number of visible pages in pagination (default is 5)
  });

  if (roles.loading) return <FullLoader />;
  if (roles.error) return <Suspense error={roles.error} />;

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-5">
        <div className="table-container">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
            <div>
              <h2 className="mb-1">Roles</h2>
              <div className="text-muted">
                Kelola role, user di dalam role, dan permission yang dipakai tiap role.
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleCreate}>
              Create Role
            </button>
          </div>
          <div className="search-container mb-3">
            <SearchInput onSearch={handleSearch} defaultValue={search} />
          </div>

          {/* Table */}
          <div className="table-responsive border rounded">
            <table className="table table-hover table-vcenter mb-0" style={{ minWidth: "760px" }}>
              <thead>
                <tr>
                  <th style={{ width: 56 }}>No</th>
                  <th style={{ minWidth: 190 }}>Display Name</th>
                  <th className="d-none d-md-table-cell" style={{ minWidth: 160 }}>Name</th>
                  <th style={{ width: 120 }}>Permissions</th>
                  <th style={{ minWidth: 180 }}>Users</th>
                  <th className="text-end" style={{ width: 130 }}>Actions</th>
                </tr>
              </thead>
              {roles.data.length > 0 && (
                <tbody>
                  {roles.data.map((data, index) => {
                    return (
                      <React.Fragment key={index}>
                        <tr>
                          <td>{index + 1}</td>
                          <td>
                            <div className="fw-bold">{data.display_name}</div>
                            <div className="text-muted small d-md-none">{data.name}</div>
                          </td>
                          <td className="d-none d-md-table-cell">
                            <span className="text-muted">{data.name}</span>
                          </td>
                          <td>
                            <span className="badge bg-blue-lt text-blue">
                              {data.permissions_count || 0}
                            </span>
                          </td>

                          <td>
                            <RoleUsersPreview
                              users={data.users || []}
                              totalCount={data.users_count || data.users?.length || 0}
                            />
                          </td>

                          <td className="text-end">
                            <Dropdown>
                              <Dropdown.Toggle
                                variant="default"
                                id="dropdownMenuButton1"
                              >
                                Actions
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  href="#"
                                  onClick={(e) => {
                                    handleAssignPermission(e, data);
                                  }}
                                >
                                  Assign Permission
                                </Dropdown.Item>
                                <Dropdown.Item
                                  href="#"
                                  onClick={(e) => {
                                    handleAssignUser(e, data);
                                  }}
                                >
                                  Sync User
                                </Dropdown.Item>
                                {/* <Dropdown.Item href="#" onClick={(e) => handleUpdate(e, data)}>Edit</Dropdown.Item> */}
                                <Dropdown.Item
                                  href="#"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(data.id);
                                  }}
                                >
                                  Remove
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4">{Pagination}</div>
        </div>
      </div>
    </LayoutDashboard>
  );
};

export default RolesPage;
